import { dbAdd, dbDelete, dbGetAll, dbGetById, dbPut } from '../indexedDb.ts';

export interface LocalRecurringTransaction {
  id: number;
  user_id: number;
  description: string;
  amount: number;
  type: 'income' | 'expense';
  account_id: number;
  category_id: number;
  recurrence_type: 'fixed_monthly' | 'fixed_yearly' | 'installment';
  start_date: string; // YYYY-MM-DD
  end_date?: string; // YYYY-MM-DD
  installments?: number;
  current_installment: number;
  is_active: boolean;
  created_at: string;
  updated_at?: string;
  // Sync fields
  server_id?: number;
  last_synced_at?: string;
  needs_sync?: boolean;
}

export async function listRecurringTransactions(): Promise<LocalRecurringTransaction[]> {
  return dbGetAll<LocalRecurringTransaction>('recurring_transactions');
}

export async function getRecurringTransactionById(id: number): Promise<LocalRecurringTransaction | undefined> {
  return dbGetById<LocalRecurringTransaction>('recurring_transactions', id);
}

export async function getActiveRecurringTransactions(): Promise<LocalRecurringTransaction[]> {
  const all = await listRecurringTransactions();
  const now = new Date().toISOString().split('T')[0];
  return all.filter(rt => {
    if (!rt.is_active) return false;
    if (rt.start_date > now) return false;
    if (rt.end_date && rt.end_date < now) return false;
    return true;
  });
}

export async function getRecurringTransactionsByDateRange(startDate: string, endDate: string): Promise<LocalRecurringTransaction[]> {
  const all = await listRecurringTransactions();
  return all.filter(rt => {
    // Incluir se começa antes ou durante o range
    if (rt.start_date > endDate) return false;
    // Se tem end_date, deve ser depois do startDate do range
    if (rt.end_date && rt.end_date < startDate) return false;
    return true;
  });
}

export async function createRecurringTransaction(data: Omit<LocalRecurringTransaction, 'id' | 'created_at' | 'updated_at' | 'server_id' | 'last_synced_at' | 'needs_sync' | 'current_installment'>) {
  const now = new Date().toISOString();
  return dbAdd('recurring_transactions', { 
    ...data, 
    current_installment: 0,
    created_at: now, 
    needs_sync: true 
  } as any);
}

export async function updateRecurringTransaction(id: number, data: Partial<LocalRecurringTransaction>) {
  const current = await dbGetById<LocalRecurringTransaction>('recurring_transactions', id);
  if (!current) throw new Error('Recurring transaction not found');
  const updated = { ...current, ...data, id, updated_at: new Date().toISOString(), needs_sync: true };
  await dbPut('recurring_transactions', updated);
  return updated;
}

export async function removeRecurringTransaction(id: number) {
  return dbDelete('recurring_transactions', id);
}

export async function markRecurringSynced(id: number, serverId: number) {
  const current = await dbGetById<LocalRecurringTransaction>('recurring_transactions', id);
  if (!current) throw new Error('Recurring transaction not found');
  current.server_id = serverId;
  current.last_synced_at = new Date().toISOString();
  current.needs_sync = false;
  await dbPut('recurring_transactions', current);
  return current;
}